import { useState } from "react";
import { Typography, Stack, Chip, Collapse, IconButton, Box } from "@mui/material";
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Cliente } from "@/shared/types/cliente";

interface ClienteInfoCardProps {
    cliente: Cliente | null;
    title?: string;
}

const getTipoDocumentoLabel = (tipo?: number) => {
    switch (tipo) {
        case 1: return "Cédula Paraguaya";
        case 2: return "Pasaporte";
        case 3: return "Cédula Extranjera";
        case 4: return "Carnet de Residencia";
        case 5: return "Innominado";
        case 6: return "Tarjeta Diplomática";
        case 9: return "Otro";
        default: return "Documento";
    }
};

export default function ClienteInfoCard({
    cliente,
    title
}: ClienteInfoCardProps) {
    const [expanded, setExpanded] = useState(false);

    if (!cliente) {
        return null;
    }

    const esContribuyente = cliente.naturaleza === 1; // NaturalezaReceptorEnum.Contribuyente
    const isPersonaFisica = cliente.tipoContribuyente === 1;

    return (
        <Box sx={{ mt: 0.5 }}>
            <Stack
                direction="row"
                alignItems="center"
                gap={0.25}
                sx={{
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.7 }
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <IconButton size="small" sx={{ p: 0.25 }}>
                    {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                </IconButton>
                <Typography variant="caption" color="primary" sx={{ textDecoration: 'underline' }}>
                    {expanded ? `Ocultar datos${title ? ` del ${title}` : ''}` : `Ver datos${title ? ` del ${title}` : ''}`}
                </Typography>
            </Stack>

            <Collapse in={expanded}>
                <Stack
                    spacing={0.5}
                    sx={{
                        pl: 1,
                        pt: 0.5,
                        pb: 0.5,
                        mt: 0.5,
                        borderLeft: 2,
                        borderColor: esContribuyente ? 'primary.main' : 'warning.main',
                        bgcolor: 'action.hover',
                        borderRadius: '0 4px 4px 0'
                    }}
                >
                    <Typography variant="body2" fontWeight="medium">
                        {cliente.nombre}
                    </Typography>

                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                        {esContribuyente ? (
                            <>
                                <Chip
                                    size="small"
                                    icon={isPersonaFisica ? <PersonIcon /> : <BusinessIcon />}
                                    label={isPersonaFisica ? "Persona Física" : "Persona Jurídica"}
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                    variant="outlined"
                                    color="primary"
                                />
                                <Typography variant="caption" color="text.secondary">
                                    RUC: {cliente.ruc}-{cliente.dv}
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Chip
                                    size="small"
                                    icon={<BadgeIcon />}
                                    label="No Contribuyente"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                    variant="outlined"
                                    color="warning"
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {getTipoDocumentoLabel(cliente.tipoDocumento)}: {cliente.numDocumento || "0"}
                                </Typography>
                            </>
                        )}
                    </Stack>

                    {cliente.email && (
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            <EmailIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                            <Typography variant="caption" color="text.secondary">
                                {cliente.email}
                            </Typography>
                        </Stack>
                    )}

                    {(cliente.telefono || cliente.celular) && (
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            <PhoneIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                            <Typography variant="caption" color="text.secondary">
                                {cliente.telefono || cliente.celular}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Collapse>
        </Box>
    );
}
